// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import actionsSubscriptions from '../../api/actionsSubscriptions';
// Components
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import DownloadCodesTable from '@/app/modules/envelopes/components/tables/DownloadCodesTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import downloadCodesFields from '@/app/modules/envelopes/fields/downloadCodesFields';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSubscription } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.subscriptionId
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalAddEnvelope
 */
function ModalAddEnvelope({ isOpen, setIsOpen, subscriptionId, setLaunchRefetch }) {
    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.add_envelope.steps';
    const downloadCodesQuery = { [downloadCodesFields.SCHEDULED_END_DATE.key]: 'NOT_IN_PAST' };

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);
    const [downloadCodeSelected, setDownloadCodeSelected] = useState(getInitialValueForRowSelected());

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.add_envelope.success' namespace={nsSubscription} />,
            variant: 'success'
        });

    };
    const onError = (error) => {
        const errorCode = error?.response?.data?.errorCode ?? error?.code;
        const erroEntity = errorCode === 'ALREADY_EXISTING_ENVELOPE' ? 'already_exists' : 'default';

        setIsOpen(false);
        toast({
            description: <Entity entity={`modal.add_envelope.error.${erroEntity}`} namespace={nsSubscription} />,
            variant: 'danger'
        });
    };
    const addEnvelope = actionsSubscriptions.addEnvelope(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_download_code`,
                disabledNext: !Object.keys(downloadCodeSelected.rowIds).length
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirm`,
                disabledNext: false
            }
        }),
        [downloadCodeSelected]
    );

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 0 ? 'max-w-6xl' : 'max-w-2xl'}>
                <HeaderTitle title='modal.add_envelope.title' namespace={nsSubscription} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSubscription}
                        className={'mb-2 flex'}
                    />
                    {step === 0 &&
                        <DownloadCodesTable
                            filtersEventName={setFiltersEventName('download_codes', true)}
                            customQuery={downloadCodesQuery}
                            singleSelection={true}
                            rowsSelected={downloadCodeSelected}
                            setRowsSelected={setDownloadCodeSelected}
                        />
                    }
                    {step === 1 &&
                        <div className='text-center'>
                            <Entity
                                entity='modal.add_envelope.confirmation.message'
                                namespace={nsSubscription}
                                data={{ downloadCode: downloadCodeSelected.rowsData[0].downloadCodeName }}
                            />
                            <div className='mt-4'>
                                <Entity entity='modal.add_envelope.confirmation.question' namespace={nsSubscription} />
                            </div>
                        </div>
                    }
                </ModalBody>
                <FooterStep
                    onValidate={
                        () => {
                            addEnvelope.mutate({
                                subscriptionId: subscriptionId,
                                downloadCodeUuid: downloadCodeSelected.rowsData[0].uuid
                            });
                        }
                    }
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    isLoading={addEnvelope.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal>
    );
}

ModalAddEnvelope.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    subscriptionId: PropTypes.string,
    setLaunchRefetch: PropTypes.func
};

export default ModalAddEnvelope;